<template>
  <div id="excalidraw" class="excalidrawCard" ref="excalidrawCard">
    <div id="excalidraw-container" ref="excalidrawContainer"></div>
  </div>
</template>

<script>
import * as ExcalidrawLib from '@excalidraw/excalidraw'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { mapState } from 'vuex'
import { throttle, isEqual, cloneDeep } from 'lodash'

// Interval between hydration retries while waiting for `canvasState`.
// NOT a fail-open timer: reaching it re-sends `requestCanvasState`, it never
// lifts the outbound publish gate on its own. See `scheduleHydrationRetry`.
const HYDRATION_RETRY_MS = 5000

export default {
  props: {
    sessionId: { type: String, required: true },
  },
  data() {
    return {
      excalidrawElementsData: [],
      excalidrawAPI: null,
      isUpdatingFromSocket: false,
      sentFileIds: new Set(),
      touchOffset: { x: 0, y: 0 },
      isCalibrated: false,

      // --- late-join hydration ---------------------------------------
      // Starts true: until this board has been hydrated we do not know
      // whether the session already has a scene, so the local (empty)
      // canvas must not be published to anyone. See the outbound gate in
      // `onChange` and `applyHydration` below.
      isHydrating: true,
      // Mirrors DocumentEditor.vue: the state request is made once, and
      // only after a `session-change` proves this socket's join actually
      // completed server-side.
      hasRequestedInitialState: false,
      // A `canvasState` that arrived before the React/Excalidraw root
      // finished mounting. Retained rather than dropped, and applied from
      // the `excalidrawAPI` callback once the API exists.
      pendingSnapshot: null,
      // Live `canvas-sync` payloads that arrived while hydration was still
      // pending (or before the API was ready). Applied in arrival order
      // AFTER the snapshot, so a newer scene always wins over it.
      queuedLiveUpdates: [],
      // Retry-only timer -- see `scheduleHydrationRetry`. This is NOT a
      // fail-open: reaching it re-requests state, it does not lift
      // `isHydrating`. Only an explicit `canvasState` response for this
      // session does that (in `applyHydration`), because a joiner
      // publishing a full scene before it genuinely knows the session's
      // existing content can destroy that content -- see the emit gate in
      // `onChange` and MATERIAL-3 in the plan this fix implements.
      hydrationRetryTimeoutId: null,
      hydrationRetryCount: 0,
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
    isAndroid() {
      return /Android/i.test(navigator.userAgent)
    },
  },
  mounted() {
    this.setupMobileOptimizations()
    this.renderExcalidraw()
    this.initializeTouchHandlers()

    // Force viewport settings for mobile
    if (this.isMobile) {
      this.setMobileViewport()
    }
  },
  methods: {
    setupMobileOptimizations() {
      if (!this.isMobile) return

      // Only prevent overscroll, not all overflow
      document.body.style.overscrollBehavior = 'none'

      // Add meta viewport adjustments
      this.updateMetaViewport()
    },

    updateMetaViewport() {
      let viewport = document.querySelector('meta[name="viewport"]')
      if (!viewport) {
        viewport = document.createElement('meta')
        viewport.name = 'viewport'
        document.head.appendChild(viewport)
      }
      viewport.content =
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
    },

    setMobileViewport() {
      // Additional viewport constraints for Android
      if (this.isAndroid) {
        const container = this.$refs.excalidrawContainer
        if (container) {
          container.style.position = 'fixed'
          container.style.top = '0'
          container.style.left = '0'
          container.style.right = '0'
          container.style.bottom = '0'
        }
      }
    },

    initializeTouchHandlers() {
      if (!this.isMobile) return

      const container = this.$refs.excalidrawCard
      if (!container) return

      // Calibration handler for first touch
      const calibrateTouch = e => {
        if (this.isCalibrated) return

        const touch = e.touches[0]

        // Calculate any offset between visual and actual touch point
        const visualX = touch.clientX
        const visualY = touch.clientY
        const actualX = touch.pageX - window.pageXOffset
        const actualY = touch.pageY - window.pageYOffset

        this.touchOffset = {
          x: actualX - visualX,
          y: actualY - visualY,
        }

        this.isCalibrated = true
      }

      // Check if touch is on canvas (not on toolbar)
      const isCanvasTouch = e => {
        // Check if the touch target is canvas or its children
        const target = e.target
        const isCanvas =
          target.tagName === 'CANVAS' ||
          target.closest('canvas') !== null ||
          (target.classList && target.classList.contains('excalidraw__canvas'))

        // Don't interfere with toolbar buttons or UI elements
        const isUIElement =
          target.closest('.Island') ||
          target.closest('.excalidraw-button') ||
          target.closest('.ToolIcon') ||
          target.closest('[role="button"]') ||
          target.closest('[role="radiogroup"]') ||
          target.closest('.dropdown') ||
          target.closest('.color-picker') ||
          target.closest('.mobile-toolbar') ||
          target.closest('.mobile-misc-tools-container')

        return isCanvas && !isUIElement
      }

      // Custom touch event normalization
      const normalizeTouchEvent = e => {
        // Only normalize if it's a canvas touch
        if (!isCanvasTouch(e)) return

        if (!e.touches || e.touches.length === 0) return

        const touch = e.touches[0]

        // Apply offset correction
        const correctedX = touch.clientX - this.touchOffset.x
        const correctedY = touch.clientY - this.touchOffset.y

        // Create normalized pointer event
        const pointerEvent = new PointerEvent(
          e.type.replace('touch', 'pointer'),
          {
            bubbles: true,
            cancelable: true,
            view: window,
            detail: 1,
            pointerId: touch.identifier,
            pointerType: 'touch',
            isPrimary: true,
            clientX: correctedX,
            clientY: correctedY,
            screenX: touch.screenX,
            screenY: touch.screenY,
            pageX: touch.pageX - this.touchOffset.x,
            pageY: touch.pageY - this.touchOffset.y,
          }
        )

        // Dispatch normalized event
        if (e.target) {
          e.target.dispatchEvent(pointerEvent)
        }

        // Only prevent default for canvas touches
        e.preventDefault()
      }

      // Enhanced touch handlers
      container.addEventListener(
        'touchstart',
        e => {
          // Only calibrate and normalize for canvas touches
          if (isCanvasTouch(e)) {
            calibrateTouch(e)
            if (this.isAndroid) {
              normalizeTouchEvent(e)
            }
          }
        },
        { passive: false }
      )

      container.addEventListener(
        'touchmove',
        e => {
          // Only normalize for canvas touches
          if (this.isAndroid && isCanvasTouch(e)) {
            normalizeTouchEvent(e)
          }
        },
        { passive: false }
      )

      container.addEventListener(
        'touchend',
        e => {
          // Only prevent default for canvas touches
          if (this.isAndroid && isCanvasTouch(e)) {
            e.preventDefault()
          }
        },
        { passive: false }
      )

      // Prevent context menu on long press (only on canvas)
      container.addEventListener('contextmenu', e => {
        if (isCanvasTouch(e)) {
          e.preventDefault()
          return false
        }
      })
    },

    renderExcalidraw() {
      const locale = this.$i18n.locale
      const App = () =>
        React.createElement(
          'div',
          {
            style: {
              height: this.isMobile ? '100vh' : '88vh',
              width: '100%',
              position: 'relative',
              touchAction: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              userSelect: 'none',
            },
          },
          React.createElement(ExcalidrawLib.Excalidraw, {
            initialData: {
              elements: this.excalidrawElementsData,
              appState: {
                zenModeEnabled: false,
                viewModeEnabled: false,
                // Mobile-specific settings
                ...(this.isMobile && {
                  gridSize: 0,
                  showStats: false,
                  theme: 'light',
                  scrollBehavior: 'disable',
                }),
              },
            },
            excalidrawAPI: api => {
              this.excalidrawAPI = api
              // Apply mobile-specific API configurations
              if (this.isMobile && api) {
                this.configureMobileAPI(api)
              }
              // The React root mounts asynchronously, so a `canvasState` or
              // `canvas-sync` can land before this callback ever runs. Those
              // were retained rather than dropped (the handlers queue them
              // instead of returning early on a missing API) -- this is the
              // point at which they become applicable.
              this.applyHydration()
              this.flushQueuedLiveUpdates()
            },
            langCode: locale === 'fa' ? 'fa-IR' : locale,
            onChange: throttle(
              elements => {
                // Hydration emit gate. Until this board knows what the
                // session already contains, it must not publish its own
                // scene. Every emit is a FULL element array and every
                // receiver applies it with a wholesale `updateScene`, so a
                // late joiner drawing one stroke on a not-yet-hydrated
                // canvas would emit `elements: [thatStroke]` and DESTROY
                // the established board for everyone else. Suppressing the
                // emit (rather than the drawing) keeps the local canvas
                // responsive; the scene is reconciled the moment the
                // snapshot arrives.
                if (this.isHydrating) return
                if (
                  this.isUpdatingFromSocket ||
                  isEqual(elements, this.excalidrawElementsData)
                ) {
                  return
                }

                this.excalidrawElementsData = cloneDeep(elements)

                const appState = this.excalidrawAPI.getAppState()
                const files = this.excalidrawAPI.getFiles()
                const newFileArray = []
                Object.keys(files).forEach(fileId => {
                  if (!this.sentFileIds.has(fileId)) {
                    newFileArray.push({
                      id: fileId,
                      dataURL: files[fileId].dataURL,
                      mimeType: files[fileId].mimeType,
                      created: files[fileId].created,
                    })
                    this.sentFileIds.add(fileId)
                  }
                })

                this.$socket.emit('canvas-sync', {
                  sessionId: this.sessionId,
                  elements: this.excalidrawElementsData,
                  appState: {
                    scrollX: appState.scrollX,
                    scrollY: appState.scrollY,
                    zoom: appState.zoom,
                  },
                  files: newFileArray,
                })
              },
              this.isMobile ? 500 : 1000
            ), // Faster sync on mobile
            // Mobile-optimized props
            ...(this.isMobile && {
              handleKeyboardGlobally: false,
              autoFocus: false,
              detectScroll: false,
              renderOptimized: true,
            }),
          })
        )

      const excalidrawWrapper = document.getElementById('excalidraw-container')
      if (excalidrawWrapper) {
        const root = createRoot(excalidrawWrapper)
        root.render(React.createElement(App))
      }
    },

    configureMobileAPI(api) {
      // Set mobile-friendly defaults
      setTimeout(() => {
        api.updateScene({
          appState: {
            currentItemStrokeWidth: 2, // Thicker lines for mobile
            currentItemRoughness: 0, // Smoother lines
            gridSize: 0,
            scrollBehavior: 'disable',
            // Disable features that might interfere with touch
            penMode: false,
            penDetected: false,
            isLoading: false,
            errorMessage: null,
            draggingElement: null,
            resizingElement: null,
            rotatingElement: null,
            zoom: {
              value: 1,
            },
          },
        })
      }, 100)
    },

    scrollToContentOnMobile(elements) {
      if (!this.isMobile || !elements || elements.length === 0) return {}

      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity
      let hasVisibleElements = false

      elements.forEach(element => {
        if (
          element.x !== undefined &&
          element.y !== undefined &&
          !element.isDeleted
        ) {
          hasVisibleElements = true
          const elementMaxX = element.x + (element.width || 0)
          const elementMaxY = element.y + (element.height || 0)

          minX = Math.min(minX, element.x)
          minY = Math.min(minY, element.y)
          maxX = Math.max(maxX, elementMaxX)
          maxY = Math.max(maxY, elementMaxY)
        }
      })

      if (!hasVisibleElements || minX === Infinity) return {}

      const container = document.getElementById('excalidraw-container')
      if (!container) return {}

      const contentCenterX = (minX + maxX) / 2
      const contentCenterY = (minY + maxY) / 2
      const viewportCenterX = container.clientWidth / 2
      const viewportCenterY = container.clientHeight / 2

      return {
        scrollX: viewportCenterX - contentCenterX,
        scrollY: viewportCenterY - contentCenterY,
      }
    },

    /**
     * Applies a full scene -- either the hydration snapshot from
     * `canvasState` or a live `canvas-sync` -- to the Excalidraw canvas.
     *
     * Both carry the identical `{ elements, appState, files }` shape, so
     * one apply path serves both and the two can never drift.
     *
     * `appState` here is only ever the shared viewport triple
     * (scrollX/scrollY/zoom); the server whitelists it on write and on
     * read. `updateScene` MERGES appState, so the local user's own
     * selection, active tool and in-progress edit are left untouched.
     */
    applyScene(data) {
      if (!this.excalidrawAPI) return

      this.isUpdatingFromSocket = true

      try {
        const currentFiles = this.excalidrawAPI.getFiles()
        const incomingFiles = data.files || []

        const filesToAdd = []
        const currentFileIds = Object.keys(currentFiles)

        for (const incomingFile of incomingFiles) {
          if (!currentFileIds.includes(incomingFile.id)) {
            if (incomingFile.dataURL) {
              filesToAdd.push({
                id: incomingFile.id,
                dataURL: incomingFile.dataURL,
                mimeType: incomingFile.mimeType,
                created: incomingFile.created || Date.now(),
              })
            } else {
              console.warn(
                `Incoming file ${incomingFile.id} has no dataURL. Skipping.`
              )
            }
          }
        }

        if (filesToAdd.length > 0) {
          this.excalidrawAPI.addFiles(filesToAdd)
        }

        const incomingElements = data.elements || []
        if (!isEqual(incomingElements, this.excalidrawElementsData)) {
          this.excalidrawElementsData = cloneDeep(incomingElements)

          const mobileScrollAdjustment = this.scrollToContentOnMobile(
            incomingElements
          )
          //          const updateAppState =
          //            this.isMobile && Object.keys(mobileScrollAdjustment).length > 0
          //              ? mobileScrollAdjustment
          //              : {
          //                  scrollX: data.appState?.scrollX || 0,
          //                  scrollY: data.appState?.scrollY || 0,
          //                  zoom: data.appState?.zoom || 1,
          //                }
          const remoteAppState = {
            scrollX: data.appState?.scrollX,
            scrollY: data.appState?.scrollY,
            zoom: data.appState?.zoom,
          }
          const hasRemoteScroll =
            typeof remoteAppState.scrollX === 'number' &&
            typeof remoteAppState.scrollY === 'number'

          let updateAppState
          if (this.isMobile) {
            if (hasRemoteScroll) {
              updateAppState = {
                scrollX: remoteAppState.scrollX,
                scrollY: remoteAppState.scrollY,
                zoom: remoteAppState.zoom ?? 1,
              }
            } else if (Object.keys(mobileScrollAdjustment).length > 0) {
              updateAppState = mobileScrollAdjustment
            } else {
              updateAppState = {
                scrollX: 0,
                scrollY: 0,
                zoom: remoteAppState.zoom ?? 1,
              }
            }
          } else {
            updateAppState = {
              scrollX: remoteAppState.scrollX ?? 0,
              scrollY: remoteAppState.scrollY ?? 0,
              zoom: remoteAppState.zoom ?? 1,
            }
          }
          this.excalidrawAPI.updateScene({
            elements: this.excalidrawElementsData,
            appState: updateAppState,
          })
        }
      } catch (error) {
        console.error('Error processing canvas-sync data:', error)
      } finally {
        this.$nextTick(() => {
          this.isUpdatingFromSocket = false
        })
      }
    },

    /**
     * Applies a retained snapshot, then every live update that arrived
     * while it was in flight, then lifts the outbound gate.
     *
     * A no-op until BOTH the snapshot has arrived and the Excalidraw API
     * exists, so it is safe to call from either trigger -- the
     * `canvasState` handler or the `excalidrawAPI` mount callback,
     * whichever happens second.
     */
    applyHydration() {
      if (!this.pendingSnapshot || !this.excalidrawAPI) return

      const snapshot = this.pendingSnapshot
      this.pendingSnapshot = null
      this.applyScene(snapshot)

      // Ordering matters: the snapshot is by definition the OLDEST scene
      // here, so anything that arrived after it is applied on top, in
      // arrival order. Draining before clearing `isHydrating` also means
      // no queued update can be lost to a concurrent flush.
      this.flushQueuedLiveUpdates()
      this.finishHydration()
    },

    flushQueuedLiveUpdates() {
      if (!this.excalidrawAPI || !this.queuedLiveUpdates.length) return
      const queued = this.queuedLiveUpdates
      this.queuedLiveUpdates = []
      for (const update of queued) {
        this.applyScene(update)
      }
    },

    /**
     * Sends (or re-sends) the hydration request and arms the retry timer.
     *
     * The retry timer is a LIVENESS aid, not a correctness escape hatch: on
     * firing it only re-requests state and reschedules itself. It never
     * sets `isHydrating = false` and never enables `onChange` to publish --
     * that happens exclusively in `finishHydration`, called only once a
     * genuine `canvasState` for this session has actually arrived (whether
     * that carries a real snapshot or the server's own explicit
     * empty-state response). A late joiner must never publish an
     * authoritative full scene while it still does not know what the
     * session already contains.
     *
     * This can retry indefinitely if the server never responds (e.g. a
     * sustained outage) -- the board stays unable to publish for as long as
     * that lasts. That is the deliberate trade this makes: never risk
     * silently destroying a peer's board, even at the cost of this client's
     * own liveness.
     */
    requestCanvasState() {
      this.$socket.emit('requestCanvasState', { sessionId: this.sessionId })
      this.scheduleHydrationRetry()
    },

    scheduleHydrationRetry() {
      this.clearHydrationRetryTimer()
      this.hydrationRetryTimeoutId = setTimeout(() => {
        if (!this.isHydrating) return
        this.hydrationRetryCount += 1
        console.warn(
          `Whiteboard hydration still pending after ${HYDRATION_RETRY_MS}ms; re-requesting canvas state`,
          { sessionId: this.sessionId, attempt: this.hydrationRetryCount }
        )
        this.requestCanvasState()
      }, HYDRATION_RETRY_MS)
    },

    clearHydrationRetryTimer() {
      if (this.hydrationRetryTimeoutId) {
        clearTimeout(this.hydrationRetryTimeoutId)
        this.hydrationRetryTimeoutId = null
      }
    },

    /**
     * Lifts the outbound publish gate. Called ONLY from `applyHydration`,
     * i.e. only once an explicit `canvasState` for this session has
     * actually been received -- never from the retry timer.
     */
    finishHydration() {
      this.isHydrating = false
      this.clearHydrationRetryTimer()
      this.flushQueuedLiveUpdates()
    },
  },

  sockets: {
    /**
     * The trustworthy confirmation that THIS socket's join for THIS
     * session succeeded server-side: `sockets.ts`'s `join` handler sets
     * `socket.data.currentSessionId` strictly before emitting
     * `session-change`, so receiving a matching one proves the backend's
     * `isCurrentSessionAuthorized` guard will now accept a
     * `requestCanvasState` for this session.
     *
     * Matched against the EVENT PAYLOAD's own `_id`/`id` rather than Vuex:
     * `App/index.vue` also listens for this event and updates
     * `state.user.session` independently, and vue-socket.io does not
     * guarantee listener ordering between components. Identical reasoning
     * to DocumentEditor.vue's `requestQuillState` gate.
     */
    'session-change'(sessionData) {
      if (this.hasRequestedInitialState) return
      if (
        sessionData._id !== this.sessionId &&
        sessionData.id !== this.sessionId
      )
        return
      this.hasRequestedInitialState = true
      this.requestCanvasState()
    },

    canvasState(data) {
      if (!data || data.sessionId !== this.sessionId) return
      // Retained rather than applied directly: the Excalidraw API may not
      // exist yet (the React root mounts asynchronously). `applyHydration`
      // is a no-op until it does, and the `excalidrawAPI` callback calls it
      // again -- so this can never be silently dropped.
      this.pendingSnapshot = data
      this.applyHydration()
    },

    'canvas-sync'(data) {
      // Task 02 / STEP-09B (N-1): recipient-side defense in depth. The
      // backend's own authorization (socket.data.currentSessionId +
      // room membership) is the real boundary; this additionally protects
      // against ANY stray canvas-sync this mounted instance might still
      // receive for a session other than the one it is currently
      // rendering (its own historical room membership, a stale broadcast,
      // etc.) before it can mutate the Excalidraw scene.
      if (data.sessionId !== this.sessionId) return

      // Previously `if (!this.excalidrawAPI) return` -- a silent drop with
      // no retry, and `canvas-sync` is the only live event, so a payload
      // lost in the React mount window stayed lost until the next human
      // mutation. Queue instead of dropping, in both the not-yet-mounted
      // and mid-hydration cases.
      if (this.isHydrating || !this.excalidrawAPI) {
        this.queuedLiveUpdates.push(data)
        return
      }

      this.applyScene(data)
    },
  },

  beforeDestroy() {
    this.clearHydrationRetryTimer()

    // Clean up mobile optimizations
    if (this.isMobile) {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
      document.body.style.overscrollBehavior = ''
    }

    const excalidrawWrapper = document.getElementById('excalidraw-container')
    if (excalidrawWrapper && excalidrawWrapper._reactRootContainer) {
      excalidrawWrapper._reactRootContainer.unmount()
    }
  },
}
</script>

<style>
.excalidrawCard,
#excalidraw-container {
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  -ms-touch-action: none;
  touch-action: none;
  height: 100%;
  width: 100%;
  overscroll-behavior: none;
  overflow: hidden;
  position: relative;
}

/* Mobile-specific styles */
@media (max-width: 768px) {
  .excalidrawCard {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    height: 100dvh !important; /* Dynamic viewport height for mobile */
  }

  #excalidraw-container {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    pointer-events: auto !important;
    /* Ensure canvas is properly positioned */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    will-change: transform;
  }

  /* Ensure toolbars and UI elements are clickable */
  .Island,
  .excalidraw-button,
  .ToolIcon,
  [role='button'],
  [role='radiogroup'],
  .dropdown,
  .color-picker,
  .mobile-toolbar,
  .mobile-misc-tools-container,
  .excalidraw .Island,
  .excalidraw__tools {
    pointer-events: auto !important;
    z-index: 10 !important;
    touch-action: manipulation !important;
  }

  /* Fix for Android Chrome specifically */
  .excalidraw-container canvas {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    touch-action: none !important;
    -webkit-touch-callout: none !important;
  }
}

/* Prevent rubber band scrolling on iOS */
html,
body {
  overscroll-behavior-y: none;
}

/* Additional Android-specific fixes */
@supports (-webkit-appearance: none) and (not (-ms-ime-align: auto)) {
  .excalidrawCard {
    /* Android-specific optimizations */
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  }
}
</style>
